import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';

import { Button } from '../../ui/atoms/Button';
import { Dialog } from '@/components/ui/organisms/Dialog';
import {
  JobFlowDialogChrome,
  jobFlowDialogBodyClassName,
  jobFlowDialogClassName,
  jobFlowDialogContentClassName,
} from './jobFlowModalChrome';
import { useJobOffer } from '../../../hooks/useJobOffers';
import { JobStatus } from '../../../types/job.types';
import {
  uploadDocument,
  analyzeDocuments,
  updateCustomInstructions,
  analyzeJobFromText,
} from '../../../services/job.service';
import { BLOCKING_FIELDS } from './fieldConfig';
import { getFieldValue, RECOMMENDED_FIELDS } from '../../../utils/missingFieldsDetection';

import { InputState } from './InputState';
import { AnalyzingState } from './AnalyzingState';
import { ReviewState } from './ReviewState';
import { SuccessState } from './SuccessState';

type ModalPhase = 'input' | 'analyzing' | 'review' | 'success' | 'failed';

interface CreateJobModalProps {
  onClose: () => void;
  onJobCreated?: () => void;
  /** When provided, the modal skips title input and uses the existing job. */
  jobId?: string;
  initialPhase?: 'input' | 'review';
  initialFile?: File | null;
  initialDescription?: string;
  onDraftChange?: (draft: { file: File | null; description: string }) => void;
}

const DEFAULT_ANALYSIS_ERROR_KEY = 'create_job_modal.default_analysis_error';

function computeMissingFields(job: ReturnType<typeof useJobOffer>['data']): {
  missingFields: string[];
  criticalMissingFields: string[];
} {
  if (!job) return { missingFields: [], criticalMissingFields: [] };
  const missing: string[] = [];
  for (const field of [...BLOCKING_FIELDS, ...RECOMMENDED_FIELDS]) {
    const value = getFieldValue(job, field);
    if (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
      missing.push(field);
    }
  }
  const criticalMissing = missing.filter((f) => (BLOCKING_FIELDS as readonly string[]).includes(f));
  return { missingFields: missing, criticalMissingFields: criticalMissing };
}

export const CreateJobModal: React.FC<CreateJobModalProps> = ({
  onClose,
  onJobCreated,
  jobId: existingJobId,
  initialPhase = 'input',
  initialFile = null,
  initialDescription = '',
  onDraftChange,
}) => {
  const { t } = useTranslation('dashboard');
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();

  const [phase, setPhase] = useState<ModalPhase>(initialPhase);
  const [file, setFile] = useState<File | null>(initialFile);
  const [description, setDescription] = useState(initialDescription);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(existingJobId ?? null);
  const [previousStatus, setPreviousStatus] = useState<string | null>(null);

  const isAnalyzing = phase === 'analyzing';
  const { data: job } = useJobOffer(jobId ?? '', {
    refetchInterval: jobId && (isAnalyzing || phase === 'review') ? 2000 : false,
  });

  // Drive transitions out of the "analyzing" state by watching job.status.
  useEffect(() => {
    if (phase !== 'analyzing' || !job) return;

    if (job.status === JobStatus.ACTIVE) {
      onJobCreated?.();
      const { missingFields, criticalMissingFields } = computeMissingFields(job);
      if (job.requiresReview || missingFields.length > 0 || criticalMissingFields.length > 0) {
        setPhase('review');
      } else {
        setPhase('success');
      }
      setPreviousStatus(null);
      return;
    }

    if (
      job.status === JobStatus.DRAFT &&
      previousStatus === JobStatus.ANALYZING &&
      (job.documents?.length ?? 0) > 0
    ) {
      const allProcessed = job.documents.every(
        (d) => d.processingStatus === 'completed' || d.processingStatus === 'failed'
      );
      if (allProcessed) {
        setPhase('failed');
        setAnalysisError(t(DEFAULT_ANALYSIS_ERROR_KEY));
        setPreviousStatus(null);
        return;
      }
    }

    if (previousStatus !== job.status) {
      setPreviousStatus(job.status);
    }
  }, [phase, job, previousStatus, onJobCreated, t]);

  const { missingFields, criticalMissingFields } = useMemo(() => computeMissingFields(job), [job]);

  useEffect(() => {
    onDraftChange?.({ file, description });
  }, [file, description, onDraftChange]);

  useEffect(() => {
    if (file || description.trim().length > 0) return;
    const backendDescription = (job?.customInstructions || job?.description || '').trim();
    if (!backendDescription) return;
    setDescription(backendDescription);
  }, [job?.customInstructions, job?.description, file, description]);

  const startAnalysis = useCallback(async () => {
    if (!file && !description.trim()) {
      toast.error(t('create_job_modal.upload_or_describe'));
      return;
    }

    setAnalysisError(null);
    setPhase('analyzing');

    try {
      const token = await getAccessTokenSilently();
      let currentJobId = jobId;

      if (!currentJobId) {
        const fallbackTitle = t('create_job_modal.untitled_job');
        const initialTitle = (job?.title || '').trim() || fallbackTitle;
        const res = await fetch(`${import.meta.env.VITE_API_SERVER_URL}/api/jobs/offers`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            title: initialTitle,
            description: '',
            status: JobStatus.DRAFT,
            is_active: true,
          }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body?.message || t('create_job_modal.failed_create_job'));
        }
        const created = await res.json();
        currentJobId = created.id;
        setJobId(currentJobId);
      }

      if (!currentJobId) throw new Error(t('create_job_modal.failed_obtain_job_id'));

      if (!file) {
        // Fast path: typed / dictated description, no file. Skips upload +
        // document classification + aggregation on the backend.
        const r = await analyzeJobFromText(token, currentJobId, description.trim());
        if (r.error) throw new Error(r.error);
      } else {
        // File path: upload, optionally store the textarea as custom instructions,
        // then trigger the existing analyze-documents pipeline.
        if (description.trim()) {
          const ci = await updateCustomInstructions(token, currentJobId, description.trim());
          if (ci.error) throw new Error(ci.error);
        }
        const up = await uploadDocument(token, currentJobId, file);
        if (up.error) throw new Error(up.error);
        const analyze = await analyzeDocuments(token, currentJobId);
        if (analyze.error) throw new Error(analyze.error);
      }
    } catch (e) {
      setPhase('failed');
      const msg = e instanceof Error ? e.message : t('create_job_modal.unknown_error');
      setAnalysisError(msg);
      toast.error(msg);
    }
  }, [file, description, getAccessTokenSilently, jobId, job?.title, t]);

  const handleReviewComplete = useCallback(() => {
    setPhase('success');
  }, []);

  const handleOpenJob = useCallback(() => {
    if (jobId) navigate(`/job/${jobId}`);
    onClose();
  }, [jobId, navigate, onClose]);

  // Esc to close — but never during analyzing (avoid orphaning the user mid-action).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && phase !== 'analyzing') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [phase, onClose]);

  const headerTitle =
    phase === 'success'
      ? t('create_job_modal.header_live')
      : phase === 'review'
      ? t('create_job_modal.header_refine')
      : existingJobId
      ? t('create_job_modal.header_add_description')
      : t('create_job_modal.header_create');

  // Visual stepper across the 3 logical phases of the flow.
  // 'analyzing' and 'failed' still live under "Details"; 'review' = Refine; 'success' = Share.
  const stepperSteps = [
    { key: 'details', label: t('create_job_modal.step_details') },
    { key: 'refine', label: t('create_job_modal.step_refine') },
    { key: 'share', label: t('create_job_modal.step_share') },
  ];
  const stepperCurrent =
    phase === 'review' ? 'refine' : phase === 'success' ? 'share' : 'details';

  const isReviewPhase = phase === 'review' && Boolean(jobId);
  const canDismiss = phase !== 'analyzing';

  return (
    <Dialog
      open
      onOpenChange={(nextOpen) => {
        if (!nextOpen && canDismiss) onClose();
      }}
      title={headerTitle}
      maxWidth="2xl"
      backdropZIndex={100}
      closeOnBackdropClick={canDismiss}
      className={jobFlowDialogClassName}
      contentClassName={jobFlowDialogContentClassName}
    >
      <JobFlowDialogChrome
        title={headerTitle}
        closeAriaLabel={t('create_job_modal.close')}
        showCloseButton={canDismiss}
        stepperSteps={stepperSteps}
        stepperCurrent={stepperCurrent}
      />

      {isReviewPhase ? (
        <ReviewState
          jobId={jobId!}
          job={job ?? null}
          missingFields={missingFields}
          criticalMissingFields={criticalMissingFields}
          isOpen
          onComplete={handleReviewComplete}
        />
      ) : (
        <Dialog.Body className={jobFlowDialogBodyClassName}>
          {phase === 'input' && (
            <InputState
              file={file}
              onFileChange={setFile}
              description={description}
              onDescriptionChange={setDescription}
              onSubmit={startAnalysis}
              onCancel={onClose}
            />
          )}

          {phase === 'analyzing' && <AnalyzingState />}

          {phase === 'success' && jobId && (
            <SuccessState jobId={jobId} onOpenJob={handleOpenJob} />
          )}

          {phase === 'failed' && (
            <div className="py-8 text-center space-y-4">
              <p className="text-sm text-gray-700 font-body max-w-md mx-auto">
                {analysisError ?? t(DEFAULT_ANALYSIS_ERROR_KEY)}
              </p>
              <div className="flex justify-center gap-3">
                <Button
                  label={t('create_job_modal.close')}
                  onClick={onClose}
                  type="outlined"
                  intent="secondary"
                />
                <Button
                  label={t('create_job_modal.try_again')}
                  onClick={() => {
                    setPhase('input');
                    setAnalysisError(null);
                  }}
                  type="contained"
                  intent="primary"
                />
              </div>
            </div>
          )}
        </Dialog.Body>
      )}
    </Dialog>
  );
};
